<template>
    <v-dialog scrollable v-model="csvEditor" width="80%">
        <template v-slot:activator="{ on, attrs }">
            <v-btn small v-bind="attrs" v-on="on">
                <v-icon small class="mr-1" color="grey">fas fa-edit</v-icon>
                Bulk Edit as CSV
            </v-btn>
        </template>
        <v-card>

            <v-card-title>
                <span>Bulk Edit as CSV</span>
            </v-card-title>

            <v-card-text>
                <v-container fluid>


                    <p class="modal-label">Update or paste the CSV below.</p>
                    <small>Item fields:</small> <code>{{ boxesFields.join(',') }}</code>


                    <div class="textarea-wrapper">
                        <!-- Line Numbers -->
                        <div class="line-numbers" ref="lineNumbers">
                            <div v-for="n in lineCount" :key="n">{{ n }}</div>
                        </div>

                        <!-- Textarea -->
                        <v-textarea
                            v-model="boxSettingsCsv"
                            :disabled="isDisabled"
                            @input="onCsvChange"
                            hide-details
                            solo
                            auto-grow
                            class="custom-textarea"
                            ref="textarea"
                            rows="10"
                            >
                        </v-textarea>
                    </div>

                </v-container>
            </v-card-text>

            <v-alert
                v-if="!isValidCsv && errorMessage !== ''"
                dense
                outlined
                type="error"
                class="validation-message"
                >
                {{ errorMessage }}
            </v-alert>

            <v-card-actions class="pb-3">
                <input
                    ref="fileInput"
                    type="file"
                    @change="importCsvFile"
                    accept=".csv"
                    style="display: none"
                />
                <v-btn :disabled="isDisabled" small @click="triggerFileInput"><v-icon small class="mr-1" color="grey">mdi-file-import</v-icon> Import CSV</v-btn>
                <v-btn :disabled="isDisabled" small @click="exportCsv"><v-icon small class="mr-1" color="grey">mdi-file-export</v-icon> Export CSV</v-btn>
                <small class="text-sm text-caption ml-4">Note: Importing will replace the current package list.</small>
                <v-spacer></v-spacer>
                <v-btn small @click="csvEditor = false">Close</v-btn>
                <v-btn small color="success" @click="saveBoxSettings" :disabled="isDisabled || (!this.isValidCsv && errorMessage !== '')">
                    <v-progress-circular
                        v-if="isSaving"
                        indeterminate
                        size="12"
                        width="2"
                        color="green darken-3"
                        class="mr-2">
                    </v-progress-circular>
                    {{ isSaving ?'Loading':'Load' }} Box Settings
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style scoped>
.v-dialog__container {
    display: unset;
}

.validation-message {
    position: sticky;
    bottom: 0;
    margin: 16px 16px 5px 16px;
    gap: 8px;
  }

.textarea-wrapper {
    display: flex;
    font-size: 14px;
    line-height: 1.5;
    border: 1px solid #ccc;
    border-radius: 4px;
    overflow: hidden;
    background-color: #f4f4f4;
    height: auto;
}

.line-numbers {
    padding: 8px 12px;
    background-color: #f4f4f4;
    border-right: 1px solid #ccc;
    text-align: right;
    user-select: none;
    white-space: nowrap;
}

.custom-textarea ::v-deep(textarea) {
    font-size: 14px;
    line-height: 1.5;
    padding: 0;
    white-space: pre; /* No text wrapping */
    overflow-x: auto; /* Horizontal scroll */
    resize: none;
    min-height: auto;
    padding-bottom: 20px;
}

</style>

<script>
export default {
    props: {
        value: Boolean, // for v-model binding
        boxSettings: Array,
        boxesFields: Array,
        validationRules: Object
    },

    data() {
        return {
            csvEditor: false,
            boxSettingsCsv: '',
            isSaving: false,
            isValidating: false,
            parsedData: [],
            errorMessage: '',
            lineCount: 1
        }
    },

    computed: {
        // Convert the box settings to CSV format
        isValidCsv() {
            return !this.isSaving && this.parsedData.length > 0;
        },

        lineNumbers() {
            return Array.from({ length: this.lineCount }, (_, i) => i + 1).join('\n')
        },

        isDisabled() {
            return this.isSaving || this.isValidating;
        }
    },

    mounted() {
        this.boxSettingsCsv = this.convertToCsv();

        // dirty fix for empty csv - when adding new box
        if(this.boxSettings.length == 1) {
            let firstRow = this.boxSettingsCsv;
            firstRow = firstRow.replace(/,/g, "");
            firstRow = firstRow.replace(/,|Carton/g, "").trim()
            if(firstRow === '') {
                this.boxSettingsCsv = '';
            }
        }

        this.updateLineCount();
    },

    methods: {

        syncTextAreaAndLineHeights() {
            const textarea = this.$refs.textarea ? this.$refs.textarea.$el.querySelector("textarea") : null;
            const lineNumbers = this.$refs.lineNumbers;

            if (textarea && lineNumbers) {
                const newHeight = textarea.scrollHeight > 0 ? textarea.scrollHeight : 40;
                lineNumbers.style.height = `${newHeight}px`;
            }
        },

        updateLineCount() {
            this.lineCount = this.boxSettingsCsv.split('\n').length
            this.$nextTick(this.syncTextAreaAndLineHeights); // Ensure DOM updates before syncing heights
        },

        onCsvChange() {
            this.isSaving = false;
            this.parsedData = [];
            this.errorMessage = '';
            this.updateLineCount();
        },

        saveBoxSettings() {

            this.parsedData = [];
            this.isSaving = true;

            if(this.validateBoxes() === false) {
                this.isSaving = false;
                return;
            } else {
                this.$emit('updatePackages', this.parsedData);
            }

            this.csvEditor = false;
            this.isSaving = false;
        },

        validateBoxes() {

            if(this.boxSettingsCsv === '') {
                this.errorMessage = 'CSV is empty.';
                return false;
            }

            const rows = this.boxSettingsCsv.trim().split('\n');
            if (rows.length < 1) {
                this.errorMessage = 'Invalid CSV format.';
                return false;
            }

            // Parse rows into objects
            let errorMessage = '';

            for (const [rowIndex, row] of rows.entries()) {

                if(this.parsedData.length >= 500) {
                    this.parsedData = [];
                    this.errorMessage = 'Maximum of 500 items allowed.';
                    return false;
                }

                const lineNumber = rowIndex + 1;
                const values = row.split(',').map(val => val.replace(/^"|"$/g, '').trim());
                const box = {};

                for (const [fieldIndex, field] of this.boxesFields.entries()) {

                    const value = values[fieldIndex];

                    // validate each box
                    if(this.validationRules[field] && this.validationRules[field].required && !value) {
                        errorMessage = `Item ${field} is required but missing in line ${lineNumber}.`;
                    } else if(this.validationRules[field] && this.validationRules[field].numeric && isNaN(value)) {
                        errorMessage = `Item ${field} must be a number in line ${lineNumber}.`;
                    }

                    if (errorMessage !== '') {
                        this.parsedData = [];
                        this.errorMessage = errorMessage;
                        return false;
                    }

                    box[field] = value;
                }
                this.parsedData.push(box);
            }

            return true;
        },

        convertToCsv() {

            const fields = this.boxesFields;
            const jsonData = this.boxSettings;
            if (!Array.isArray(jsonData) || jsonData.length === 0) {
                return "";
            }

            const csvRows = [];

            jsonData.forEach(obj => {
                const row = fields.map(field => {
                    let value = obj[field] ?? ''; // Fallback to empty string if undefined
                    return String(value).trim(); // Trim spaces
                });
                csvRows.push(row.join(','));
            });

            return csvRows.join('\n');
        },

        triggerFileInput() {
            this.$refs.fileInput.click();
        },

        importCsvFile(event) {
            this.boxSettingsCsv = '';
            this.parsedData = [];
            this.updateLineCount();

            const file = event.target.files[0];
            if (!file) return;

            const reader = new FileReader();
            reader.onload = (e) => {

                let content = e.target.result;

                // remove header
                const firstRow = content.split('\n')[0].split(',');
                if (firstRow.every((val, index) => val === this.boxesFields[index]))  {
                    //remove the first row
                    content = content.split('\n').slice(1).join('\n');
                }

                this.boxSettingsCsv = content
                this.updateLineCount();
                this.validateBoxes();
            };
            reader.readAsText(file);

        },

        exportCsv() {
            const headers = this.boxesFields;


            const jsonData = this.boxSettings;
            if (!Array.isArray(jsonData) || jsonData.length === 0) {
                return;
            }

            // Add header row (field names)
            const csvRows = headers.join(',') +'\n'+ this.boxSettingsCsv;


            const blob = new Blob([csvRows], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.setAttribute('href', url);
            link.setAttribute('download', 'box_settings.csv');
            link.click();
        }
    }
}
</script>